import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { ApiError, errorMessage, fieldErrors } from "../../shared/api/errors.ts";
import { useReturnTo, withReturnTo } from "../../shared/session/returnTo.ts";
import { useSession } from "../../shared/session/SessionProvider.tsx";
import { Button } from "../../shared/ui/Button/Button.tsx";
import { Input } from "../../shared/ui/Input/Input.tsx";
import { AuthScreen } from "../auth/AuthScreen.tsx";
import styles from "./RegisterPage.module.css";

// Имена полей совпадают с RegisterRequest, чтобы ошибки валидации сервера ложились на них из `error.details`.
const SERVER_FIELDS = ["displayName", "email", "password"] as const;
type Field = (typeof SERVER_FIELDS)[number] | "passwordRepeat";
type Errors = Partial<Record<Field | "form", string>>;

// Как у backend: RegisterRequest.password min_length=6, displayName max_length=128.
const MIN_PASSWORD_LENGTH = 6;
const MAX_NAME_LENGTH = 128;

/** 03.01–03.04 / REGISTRATION. После регистрации GuestOnly уводит на исходный URL или на главную. */
export function RegisterPage() {
  const { register } = useSession();
  const loginLink = withReturnTo("/login", useReturnTo());
  const [values, setValues] = useState<Record<Field, string>>({
    displayName: "",
    email: "",
    password: "",
    passwordRepeat: "",
  });
  const [errors, setErrors] = useState<Errors>({});
  const mutation = useMutation({ mutationFn: register, onError: (error) => setErrors(errorsFrom(error)) });

  function change(field: Field, value: string) {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => {
      const next = { ...current };
      delete next[field];
      delete next.form;
      return next;
    });
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const local = validate(values);
    setErrors(local);
    if (Object.keys(local).length > 0) return;
    mutation.mutate({ displayName: values.displayName.trim(), email: values.email.trim(), password: values.password });
  }

  return (
    <AuthScreen
      title="Создайте аккаунт"
      subtitle="Это займет всего пару минут"
      footer={{ prompt: "Уже есть аккаунт?", label: "Войти", to: loginLink }}
    >
      <form className={styles.form} onSubmit={submit} noValidate>
        <Input
          label="Имя"
          name="name"
          autoComplete="name"
          placeholder="Ваше имя"
          maxLength={MAX_NAME_LENGTH}
          value={values.displayName}
          onChange={(event) => change("displayName", event.target.value)}
          error={errors.displayName}
        />
        <Input
          label="Email"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          placeholder="user@mail.ru"
          value={values.email}
          onChange={(event) => change("email", event.target.value)}
          error={errors.email}
        />
        <Input
          label="Пароль"
          type="password"
          name="password"
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
          hint="Минимум 6 символов"
          placeholder="•••••••"
          value={values.password}
          onChange={(event) => change("password", event.target.value)}
          error={errors.password}
        />
        <Input
          label="Повторите пароль"
          type="password"
          name="password-repeat"
          autoComplete="new-password"
          placeholder="•••••••"
          value={values.passwordRepeat}
          onChange={(event) => change("passwordRepeat", event.target.value)}
          error={errors.passwordRepeat}
        />
        <div className={styles.submit}>
          <Button type="submit" loading={mutation.isPending}>
            Зарегистрироваться
          </Button>
          {errors.form && (
            <p className={styles.formError} role="alert">
              {errors.form}
            </p>
          )}
        </div>
        {/* В новой вкладке: переход в этой размонтировал бы форму вместе с введёнными данными. */}
        <p className={styles.agreement}>
          Нажимая «Зарегистрироваться», вы принимаете{" "}
          <Link to="/terms" target="_blank" className={styles.agreementLink}>
            Пользовательское соглашение
          </Link>{" "}
          и соглашаетесь с{" "}
          <Link to="/privacy" target="_blank" className={styles.agreementLink}>
            Политикой конфиденциальности
          </Link>
          .
        </p>
      </form>
    </AuthScreen>
  );
}

function validate(values: Record<Field, string>): Errors {
  const errors: Errors = {};
  const email = values.email.trim();
  if (!values.displayName.trim()) errors.displayName = "Введите имя";
  if (!email) errors.email = "Введите email";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = "Введите корректный email";
  // Длину считаем в символах, как Python на backend, а не в UTF-16 единицах.
  if (!values.password) errors.password = "Введите пароль";
  else if ([...values.password].length < MIN_PASSWORD_LENGTH) errors.password = `Минимум ${MIN_PASSWORD_LENGTH} символов`;
  if (!values.passwordRepeat) errors.passwordRepeat = "Повторите пароль";
  else if (values.passwordRepeat !== values.password) errors.passwordRepeat = "Пароли не совпадают";
  return errors;
}

function errorsFrom(error: Error): Errors {
  if (error instanceof ApiError && error.code === "email_already_registered") return { email: errorMessage(error) };
  const byField = fieldErrors(error, SERVER_FIELDS);
  return Object.keys(byField).length > 0 ? byField : { form: errorMessage(error) };
}
